import { useState, type FormEvent } from 'react'
import { ImageIcon, Upload } from 'lucide-react'
import AdminDialog from '@/components/AdminDialog'
import type { CommissionUploads } from '@/api/productionCommissions'
import { errorMessage } from '@/api/client'
import ProductionCommissionCover from './ProductionCommissionCover'
import RecruitmentStatusSelect from './RecruitmentStatusSelect'
import { type ProductionCommission } from './productionCommissionsModel'

type CommissionForm = Omit<ProductionCommission, 'id' | 'applications'>

const blank: CommissionForm = {
  title: '',
  synopsis: '',
  sampleExcerpt: '',
  scriptFileName: '',
  fullScriptFileName: '',
  submissionPoints: 800,
  rateYuanPerMinute: 1000,
  totalEpisodes: 10,
  covers: [],
  recruitmentStartDate: '',
  deadline: '',
  status: 'recruiting',
}

const field = 'mt-1 w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm'

function chinaCalendarDate(value?: string): string {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date)
  const get = (type: string) => parts.find(part => part.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')}`
}

function readImage(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('封面读取失败，请重试'))
    reader.readAsDataURL(file)
  })
}

export default function ProductionCommissionEditor({
  offer,
  onClose,
  onSave,
}: {
  offer?: ProductionCommission
  onClose: () => void
  onSave: (value: ProductionCommission, uploads: CommissionUploads) => Promise<void> | void
}) {
  const [form, setForm] = useState<CommissionForm>(() => offer ? {
    title: offer.title,
    synopsis: offer.synopsis,
    sampleExcerpt: offer.sampleExcerpt,
    scriptFileName: offer.scriptFileName,
    fullScriptFileName: offer.fullScriptFileName || '',
    submissionPoints: offer.submissionPoints ?? 800,
    rateYuanPerMinute: offer.rateYuanPerMinute ?? 1000,
    totalEpisodes: offer.totalEpisodes ?? 10,
    covers: [...offer.covers],
    recruitmentStartDate: offer.recruitmentStartDate || chinaCalendarDate(offer.recruitmentStartedAt),
    deadline: offer.deadline,
    status: offer.status,
  } : blank)
  const [error, setError] = useState('')
  const [confirmChanges, setConfirmChanges] = useState(false)
  const [coverFiles, setCoverFiles] = useState<Array<{ url: string; file: File }>>([])
  const [scriptFile, setScriptFile] = useState<File>()
  const [busy, setBusy] = useState(false)
  const submissionPointsLocked = Boolean(offer?.paidParticipantCount)

  function change<K extends keyof CommissionForm>(key: K, value: CommissionForm[K]) {
    setForm((current) => ({ ...current, [key]: value }))
    setConfirmChanges(false)
  }

  async function addCovers(files: FileList | null) {
    if (!files?.length) return
    const selected = Array.from(files)
    if (form.covers.length + selected.length > 3) {
      setError('封面最多上传 3 张')
      return
    }
    if (selected.some((file) => !['image/jpeg', 'image/png'].includes(file.type) || file.size > 5 * 1024 * 1024)) {
      setError('封面须为 JPG 或 PNG，单张不超过 5 MB')
      return
    }
    try {
      const covers = await Promise.all(selected.map(readImage))
      setForm((current) => ({ ...current, covers: [...current.covers, ...covers].slice(0, 3) }))
      setCoverFiles((current) => [...current, ...selected.map((file, index) => ({ url: covers[index], file }))])
      setError('')
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : '封面读取失败')
    }
  }

  function setScript(file?: File) {
    if (!file) return
    if (!/\.(pdf|doc|docx)$/i.test(file.name) || file.size > 20 * 1024 * 1024) {
      setError('第一集试稿剧本须为 PDF、DOC 或 DOCX，单个文件不超过 20 MB')
      return
    }
    change('scriptFileName', file.name)
    setScriptFile(file)
    setError('')
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!form.title.trim() || !form.synopsis.trim() || !form.sampleExcerpt.trim() || !form.recruitmentStartDate || !form.deadline) {
      setError('请填写漫剧名称、剧本梗概、公开预览、招募开始日期和截止日期')
      return
    }
    if (form.recruitmentStartDate >= form.deadline) {
      setError('招募截止日期必须晚于开始日期，不能为同一天')
      return
    }
    if (form.status === 'recruiting' && !form.scriptFileName) {
      setError('发布招募前请上传供扣点用户下载的第一集试稿剧本')
      return
    }
    const sensitiveChanges = offer && (offer.applicationCount || offer.applications.length) > 0 &&
      (offer.sampleExcerpt !== form.sampleExcerpt || offer.scriptFileName !== form.scriptFileName || offer.submissionPoints !== form.submissionPoints)
    if (sensitiveChanges && !confirmChanges) {
      setConfirmChanges(true)
      return
    }
    setBusy(true)
    try { await onSave({
      ...form,
      title: form.title.trim(),
      synopsis: form.synopsis.trim(),
      sampleExcerpt: form.sampleExcerpt.trim(),
      id: offer?.id || `preview-${Date.now()}`,
      applications: offer?.applications || [],
      revision: offer?.revision,
    }, { covers: coverFiles.filter(item => form.covers.includes(item.url)).map(item => item.file), script: scriptFile })
    } catch (cause) { setError(errorMessage(cause)) }
    finally { setBusy(false) }
  }

  return (
    <AdminDialog title={offer ? '编辑商单' : '新增商单'} onClose={onClose} busy={busy} wide>
      <form onSubmit={submit} className="space-y-5">
        <div>
          <p className="text-sm font-medium">封面（最多 3 张）</p>
          <div className="mt-2 flex flex-wrap items-start gap-3">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={index} className="relative flex h-32 w-24 items-center justify-center overflow-hidden rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-300">
                {form.covers[index] ? <>
                  <ProductionCommissionCover id={offer?.id || ''} url={form.covers[index]} alt={`封面 ${index + 1}`} className="h-full w-full object-cover" />
                  <button type="button" aria-label={`移除封面 ${index + 1}`} onClick={() => change('covers', form.covers.filter((_, i) => i !== index))} className="absolute right-1 top-1 rounded bg-slate-900/70 px-1.5 py-0.5 text-xs text-white">×</button>
                </> : <ImageIcon size={28} aria-hidden="true" />}
              </div>
            ))}
            <div className="space-y-2 self-center">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">
                <Upload size={16} aria-hidden="true" />上传封面
                <input type="file" accept="image/jpeg,image/png" multiple className="sr-only" onChange={(event) => { void addCovers(event.target.files); event.currentTarget.value = '' }} />
              </label>
              <p className="text-xs text-slate-500">JPG、PNG · 建议 750 × 1000 · 单张不超过 5 MB</p>
            </div>
          </div>
        </div>
        <label className="block text-sm font-medium">漫剧名称
          <input className={field} required maxLength={120} placeholder="请输入漫剧名称" value={form.title} onChange={(event) => change('title', event.target.value)} />
        </label>
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm font-medium">承制报价（元/分钟）<input className={field} type="number" min="0.01" step="0.01" required value={form.rateYuanPerMinute ?? 1000} onChange={event => change('rateYuanPerMinute', Number(event.target.value))} /></label>
          <label className="text-sm font-medium">投稿扣点<input className={field} type="number" min="1" max="1000000" step="1" required disabled={submissionPointsLocked} value={form.submissionPoints ?? 800} onChange={event => change('submissionPoints', Number(event.target.value))} />{submissionPointsLocked && <span className="mt-1 block text-xs text-slate-500">已有用户扣点，投稿点数不可修改。</span>}</label>
          <label className="text-sm font-medium">计划集数<input className={field} type="number" min="1" max="200" step="1" required value={form.totalEpisodes ?? 10} onChange={event => change('totalEpisodes', Number(event.target.value))} /></label>
        </div>
        <label className="block text-sm font-medium">剧本梗概
          <textarea className={`${field} min-h-28 resize-y`} required maxLength={1000} placeholder="介绍项目内容、制作要求及合作方式" value={form.synopsis} onChange={(event) => change('synopsis', event.target.value)} />
          <span className="block text-right text-xs text-slate-400">{form.synopsis.length}/1000</span>
        </label>
        <div className="space-y-2">
          <p className="text-sm font-medium">第一集试稿剧本（扣点后下载）</p>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">
              <Upload size={16} aria-hidden="true" />上传第一集试稿剧本
              <input type="file" accept=".pdf,.doc,.docx" className="sr-only" onChange={(event) => { setScript(event.target.files?.[0]); event.currentTarget.value = '' }} />
            </label>
            <span className="text-xs text-slate-500">PDF、DOC、DOCX · 单个文件不超过 20 MB</span>
          </div>
          {form.scriptFileName && <p className="break-all text-xs text-brand-700">已选择：{form.scriptFileName}</p>}
          <label className="block text-sm font-medium">试稿片段预览（所有访客可见）</label>
          <textarea className={`${field} min-h-24 resize-y`} aria-label="试稿片段预览内容" required maxLength={1000} placeholder="请输入让用户了解剧本内容的公开预览，勿填写需要扣点后查看的剧本内容…" value={form.sampleExcerpt} onChange={(event) => change('sampleExcerpt', event.target.value)} />
          <span className="block text-right text-xs text-slate-400">{form.sampleExcerpt.length}/1000</span>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          <label className="min-w-0 text-sm font-medium">招募开始日期
            <input className={field} type="date" required value={form.recruitmentStartDate || ''} onChange={(event) => change('recruitmentStartDate', event.target.value)} />
          </label>
          <label className="min-w-0 text-sm font-medium">招募截止日期
            <input className={field} type="date" required value={form.deadline} onChange={(event) => change('deadline', event.target.value)} />
          </label>
          <RecruitmentStatusSelect value={form.status} onChange={value => change('status', value)} />
        </div>
        {offer && (offer.applicationCount || offer.applications.length) > 0 && <p className="rounded-lg bg-slate-100 p-3 text-sm text-slate-600">已有用户申请；修改试稿内容时需要再次确认。</p>}
        {confirmChanges && <p role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">已有用户申请。请确认修改试稿内容，再点击“确认保存”。</p>}
        {error && <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">取消</button>
          <button type="submit" disabled={busy} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
            {busy ? '保存中…' : confirmChanges ? '确认保存' : form.status === 'draft' ? '保存' : offer ? '保存修改' : '发布商单'}
          </button>
        </div>
      </form>
    </AdminDialog>
  )
}
